import { formatCurrency } from "@/lib/utils";
import { TrendingUp, Brain, DollarSign, Package, Zap } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface AdditionalItemsCardProps {
    itemsCount: number;
    totalValue: number;
    onViewAdditionalItems?: () => void;
}

export function AdditionalItemsCard({ itemsCount, totalValue, onViewAdditionalItems }: AdditionalItemsCardProps) {
    return (
        <Card className="border border-purple-500/20 dark:border-purple-500/20 bg-gradient-to-br from-purple-50/20 to-purple-50/10 dark:from-purple-950/10 dark:to-purple-950/5">
            <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-gradient-to-br from-purple-500/80 to-violet-500/80 rounded-lg">
                        <Package className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                        <CardTitle className="text-base font-bold text-purple-700 dark:text-purple-300">
                            AI-Suggested Additional Line Items
                        </CardTitle>
                        <Badge variant="outline" className="text-xs mt-1 border-purple-500/20 bg-purple-50/20 dark:bg-purple-900/10">
                            <Brain className="h-2.5 w-2.5 mr-1" />
                            Carrier Pre-Approved Items
                        </Badge>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="pt-0">
                {/* Metrics Grid */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-white/70 dark:bg-gray-900/50 rounded-lg p-2.5 border border-purple-200/50 dark:border-purple-800/50">
                        <div className="flex items-center gap-1 mb-0.5">
                            <DollarSign className="h-3.5 w-3.5 text-purple-600" />
                            <p className="text-xs text-muted-foreground">Total Value</p>
                        </div>
                        <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{formatCurrency(totalValue)}</p>
                        <p className="text-xs text-muted-foreground">{itemsCount} items</p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-100 to-violet-100 dark:from-purple-900/50 dark:to-violet-900/50 rounded-lg p-2.5 border border-purple-300/50 dark:border-purple-700/50">
                        <div className="flex items-center gap-1 mb-0.5">
                            <TrendingUp className="h-3.5 w-3.5 text-purple-600" />
                            <p className="text-xs text-muted-foreground">Potential Commission</p>
                        </div>
                        <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{formatCurrency(totalValue * 0.1)}</p>
                        <p className="text-xs font-semibold text-purple-600">10% ROI</p>
                    </div>
                </div>

                {/* Action button */}
                <Button
                    size="sm"
                    className="w-full bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white"
                    onClick={onViewAdditionalItems}
                    data-testid="button-view-additional"
                >
                    <Zap className="h-3.5 w-3.5 mr-1.5" />
                    Review Items
                </Button>
            </CardContent>
        </Card>
    );
}
